import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { AuthContext } from "../../../contexts/AuthContext";
import { useErrorDialog } from "../../../contexts/ErrorDialogContext";
import { useSuccessDialog } from "../../../contexts/SuccessDialogContext";
import ComponentLoading from "../../App/ComponentLoading";
import DisclosureFormDialog from "./DisclosureFormDialog";
import DisclosureTagLibraryDialog from "./DisclosureTagLibraryDialog";
import DisclosureList from "./DisclosureList";
import DisclosureBulkDeleteDialog from "./DisclosureBulkDeleteDialog";
import useExcelUpload from "../../../utils/hooks/useExcelUpload";
import UploadExcelDialog from "../../Schedules/utils/UploadExcelEnhanced";
import DisclosureInspector from "./DisclosureInspector";
import { exportDisclosuresToExcel } from "../../Schedules/utils/UploadExcelEnhanced";
import {
  getDisclosureCenterDisplayConfig,
  shouldUseLargeDisclosureCenter
} from "../../../utils/responsiveLayoutSettings";

/** Left “directory” column on desktop */
const LIST_COLUMN_WIDTH_MD = "clamp(300px, 30vw, 400px)";

function extractList(res) {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  if (Array.isArray(res.data)) return res.data;
  return [];
}

const normalizeTagNameForUpload = (value = "") =>
  String(value).trim().replace(/\s+/g, " ");

const formatBooleanCell = (value) => {
  if (value === undefined || value === null) return undefined;
  if (value === "") return undefined;
  return value;
};

const disclosureUploadInstructions = [
  ["Title", "Disclosure title. Required."],
  ["Statement Text", "Disclosure statement text. Required."],
  ["Company UEN", "Optional. Use the company UEN to link this disclosure."],
  [
    "Tag Names",
    "Optional. Comma-separated disclosure tag names (e.g. VAT, GST)."
  ],
  ["Is Active", "true or false. Controls visibility status."]
];

const disclosureUploadSampleData = [
  {
    Title: "Capital / disallowable (client)",
    "Statement Text":
      "Please confirm that there are no capital or disallowable expenses included in the above. Examples of capital or disallowable expenses includes depreciation, small assets expensed off, private car expenses, statutory penalties. If otherwise, please provide details.",
    "Company UEN": "",
    "Tag Names": "Capital expenses",
    "Is Active": "TRUE"
  },
  {
    Title: "Capital / disallowable (IRAS)",
    "Statement Text":
      "Our client confirms that there are no capital or disallowable expenses included in the above.",
    "Company UEN": "",
    "Tag Names": "Capital expenses",
    "Is Active": "TRUE"
  },
  {
    Title: "S14R",
    "Statement Text":
      "Our client reserve the right to claim S14R pre-commencement expenses on the above if the qualifying conditions are met.",
    "Company UEN": "",
    "Tag Names": "S14R",
    "Is Active": "TRUE"
  },
  {
    Title: "S14N (Client)",
    "Statement Text":
      "Please confirm that the above renovations works does not require the approval of the BCA.",
    "Company UEN": "",
    "Tag Names": "S14N Reno",
    "Is Active": "TRUE"
  },
  {
    Title: "S14N (IRAS)",
    "Statement Text":
      "Our client confirms that the above renovations works does not require the approval of the BCA.",
    "Company UEN": "",
    "Tag Names": "S14N Reno",
    "Is Active": "TRUE"
  },
  {
    Title: "Start up exemption",
    "Statement Text":
      "The client qualifies for start up exemption between YA [    ] and YA [    ].",
    "Company UEN": "",
    "Tag Names": "Start up",
    "Is Active": "TRUE"
  },
  {
    Title: "Losses / CA carried forward (Client)",
    "Statement Text":
      "Please confirm that there is no substantial change in shareholders between the relevatn dates [                         ] and [                      ]. \n\nFor capital allowances, please also confirm that the same trade test is satisfied where applicable.",
    "Company UEN": "",
    "Tag Names": "Unabsorbed loss items",
    "Is Active": "TRUE"
  },
  {
    Title: "Unremitted foreign income (Client)",
    "Statement Text":
      "Please confirm that the foreign-sourced income disclosed above remains unremitted and has not been remitted to, received in, or applied in satisfaction of any debt or business purpose in Singapore during the basis period. If otherwise, please provide details.",
    "Company UEN": "",
    "Tag Names": "Unremitted income",
    "Is Active": "TRUE"
  },
  {
    Title: "Unremitted foreign income (IRAS)",
    "Statement Text":
      "Our client confirms that the foreign-sourced income disclosed above remains unremitted and has not been remitted to, received in, or applied in satisfaction of any debt or business purpose in Singapore during the basis period.",
    "Company UEN": "",
    "Tag Names": "Unremitted income",
    "Is Active": "TRUE"
  }
];

const disclosureUploadValidationSchema = [
  { type: "text", required: true }, // Title
  { type: "text", required: true }, // Statement Text
  { type: "text", required: false }, // Company UEN
  { type: "text", required: false }, // Tag Names
  { type: "boolean", required: true } // Is Active
];

const DisclosureCenterPage = () => {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const {
    listDisclosures,
    createDisclosure,
    updateDisclosure,
    deleteDisclosure,
    batchSyncDisclosures,
    deleteDisclosuresBatch,
    listDisclosureTags,
    createDisclosureTag,
    updateDisclosureTag,
    deleteDisclosureTag,
    deleteDisclosureTagsBatch,
    getCompanyList
  } = useContext(AuthContext);

  const showError = useErrorDialog();
  const showSuccess = useSuccessDialog();

  const [loading, setLoading] = useState(true);
  const [disclosures, setDisclosures] = useState([]);

  const [filterActive, setFilterActive] = useState("all"); // all | true | false
  const [search, setSearch] = useState("");
  const [useLargeLayout, setUseLargeLayout] = useState(() =>
    shouldUseLargeDisclosureCenter()
  );
  const [toolsAnchorEl, setToolsAnchorEl] = useState(null);

  const [snack, setSnack] = useState(null);

  const [selectedDisclosure, setSelectedDisclosure] = useState(null);
  const [tagsDrawerOpen, setTagsDrawerOpen] = useState(false);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [tagsSubmitting, setTagsSubmitting] = useState(false);
  const [tags, setTags] = useState([]);
  const [tagSearch, setTagSearch] = useState("");
  const [tagNameInput, setTagNameInput] = useState("");
  const [editingTagId, setEditingTagId] = useState(null);
  const [editingTagName, setEditingTagName] = useState("");
  const [tagLibraryOpen, setTagLibraryOpen] = useState(false);
  const [tagLibrarySearch, setTagLibrarySearch] = useState("");
  const [tagLibraryNameInput, setTagLibraryNameInput] = useState("");
  const [libraryEditingTagId, setLibraryEditingTagId] = useState(null);
  const [libraryEditingTagName, setLibraryEditingTagName] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("create"); // create | edit
  const [formSubmitting, setFormSubmitting] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const [bulkDeleteIds, setBulkDeleteIds] = useState(null);
  const [bulkDeleteSubmitting, setBulkDeleteSubmitting] = useState(false);
  const [bulkDeleteSeq, setBulkDeleteSeq] = useState(0);

  const [batchProcessing, setBatchProcessing] = useState(false);
  const {
    openDialog: uploadDialogOpen,
    selectedFile,
    uploading,
    rowCount: validRows,
    errorFileUrl,
    handleOpenDialog: openUploadDialog,
    handleCloseDialog: closeUploadDialog,
    handleCloseAlert: closeUploadAlert,
    handleDownloadTemplate: downloadDisclosureTemplate,
    handleFileChange: handleUploadFileChange,
    handleResetFile: handleUploadReset,
    handleUpload: triggerUpload,
    getTemplateHeadersString
  } = useExcelUpload({
    fileName: "disclosure_batch_upload_template.xlsx",
    instructions: disclosureUploadInstructions,
    sampleData: disclosureUploadSampleData,
    validationSchema: disclosureUploadValidationSchema,
    onUpload: async (excelData) => {
      setBatchProcessing(true);

      try {
        const [companiesRes, tagsRes] = await Promise.all([
          getCompanyList(),
          listDisclosureTags()
        ]);

        const companiesRows = extractList(companiesRes);
        const tagsRows = extractList(tagsRes);

        const companyByUen = new Map(
          companiesRows.map((c) => [String(c.uen || "").trim(), c.c_id])
        );

        const tagIdByName = new Map(
          tagsRows.map((t) => [
            String(t.name || "")
              .trim()
              .toLowerCase(),
            t.t_id
          ])
        );

        const normalizeTitleKey = (value) =>
          String(value || "")
            .trim()
            .toLowerCase();

        const normalizeTagKey = (value = "") =>
          normalizeTagNameForUpload(value).toLowerCase();

        const normalizeIsActive = (value) => {
          if (value === undefined || value === null) return undefined;
          const s = String(value).trim().toLowerCase();
          if (!s) return undefined;
          if (s === "true" || s === "1" || s === "yes") return true;
          if (s === "false" || s === "0" || s === "no") return false;
          // keep undefined so backend validation can handle unexpected inputs
          return undefined;
        };

        const existingByTitleKey = new Map(
          (disclosures || [])
            .filter((d) => d && d.title)
            .map((d) => [normalizeTitleKey(d.title), d])
        );

        const ensureTagId = async (rawName) => {
          const normalized = normalizeTagNameForUpload(rawName);
          if (!normalized) return null;

          const key = normalized.toLowerCase();
          const existing = tagIdByName.get(key);
          if (existing) return existing;

          // Create missing tags (non-system).
          const res = await createDisclosureTag({
            name: normalized,
            is_system: false
          });
          const createdTag = res?.data || res;
          const t_id = createdTag?.t_id;
          if (!t_id) throw new Error("Failed to create disclosure tag");
          tagIdByName.set(key, String(t_id));
          return String(t_id);
        };

        // Group rows by company UEN (or no-company) for batch sync calls
        const rowsByCompany = new Map(); // key: c_id|"" → items[]
        const preparationErrors = [];

        for (let i = 0; i < excelData.length; i++) {
          const row = excelData[i];
          const [
            title,
            statementText,
            companyUenVal,
            tagNamesVal,
            isActiveVal
          ] = row;

          const titleStr = String(title || "").trim();
          const statementStr = String(statementText || "").trim();
          const companyUen = String(companyUenVal || "").trim();
          const isActive = formatBooleanCell(isActiveVal);

          const tagNames = String(tagNamesVal || "")
            .split(/[,;]+/g)
            .map((s) => s.trim())
            .filter(Boolean);

          try {
            const c_id =
              companyUen && companyByUen.has(companyUen)
                ? companyByUen.get(companyUen)
                : companyUen
                  ? (() => {
                      throw new Error(`Unknown company UEN: ${companyUen}`);
                    })()
                  : undefined;

            const tag_ids = [];
            for (const tagName of tagNames) {
              const t_id = await ensureTagId(tagName);
              if (t_id) tag_ids.push(t_id);
            }

            // Skip sending a no-op update if every column matches what we already have.
            // Title is treated as the identity key (org-wide uniqueness).
            const existingRow = existingByTitleKey.get(
              normalizeTitleKey(titleStr)
            );
            if (existingRow) {
              const existingStatement = String(
                existingRow.statement_text || ""
              ).trim();
              const existingUen = String(existingRow.company?.uen || "").trim();
              const existingIsActive = normalizeIsActive(existingRow.is_active);
              const incomingIsActive = normalizeIsActive(isActive);

              const existingTagSet = new Set(
                (existingRow.disclosure_tags || [])
                  .map((t) => normalizeTagKey(t?.name || ""))
                  .filter(Boolean)
              );
              const incomingTagSet = new Set(
                (tagNames || []).map((n) => normalizeTagKey(n)).filter(Boolean)
              );
              const sameTags =
                existingTagSet.size === incomingTagSet.size &&
                [...existingTagSet].every((k) => incomingTagSet.has(k));

              const same =
                existingStatement === statementStr &&
                existingUen === companyUen &&
                existingIsActive === incomingIsActive &&
                sameTags;

              if (same) continue;
            }

            const item = {
              title: titleStr,
              statement_text: statementStr,
              is_active: isActive,
              company_uen: companyUen || "",
              ...(c_id ? { c_id } : {}),
              ...(tag_ids.length > 0 ? { tag_ids } : {})
            };

            const companyKey = c_id || "";
            if (!rowsByCompany.has(companyKey)) {
              rowsByCompany.set(companyKey, { c_id, items: [] });
            }
            rowsByCompany.get(companyKey).items.push(item);
          } catch (error) {
            const reason =
              error?.response?.data?.message ||
              error?.message ||
              "Unknown error";
            preparationErrors.push({
              title: titleStr || "(no title)",
              reason
            });
          }
        }

        // Send batch sync requests grouped by company
        let totalCreated = 0;
        let totalUpdated = 0;
        const allErrors = [...preparationErrors];

        for (const [, group] of rowsByCompany) {
          try {
            const res = await batchSyncDisclosures({
              items: group.items,
              c_id: group.c_id
            });
            const result = res?.data || res;
            totalCreated += (result?.created || []).length;
            totalUpdated += (result?.updated || []).length;
            if (result?.errors?.length) {
              for (const err of result.errors) {
                allErrors.push({
                  title: err.title || "(no title)",
                  reason: err.message || "Unknown error"
                });
              }
            }
          } catch (error) {
            const reason =
              error?.response?.data?.message ||
              error?.message ||
              "Batch sync failed";
            // Mark all items in this group as failed
            for (const item of group.items) {
              allErrors.push({ title: item.title || "(no title)", reason });
            }
          }
        }

        const totalSuccessful = totalCreated + totalUpdated;
        if (allErrors.length === 0) {
          const parts = [];
          if (totalCreated > 0) parts.push(`${totalCreated} created`);
          if (totalUpdated > 0) parts.push(`${totalUpdated} updated`);
          showSuccess(
            `Successfully synced ${totalSuccessful} disclosure${totalSuccessful === 1 ? "" : "s"} (${parts.join(", ")}).`
          );
        } else if (totalSuccessful > 0) {
          const parts = [];
          if (totalCreated > 0) parts.push(`${totalCreated} created`);
          if (totalUpdated > 0) parts.push(`${totalUpdated} updated`);
          const summary = `${parts.join(", ")}. ${allErrors.length} failed:\n${allErrors
            .map((f) => `${f.title}: ${f.reason}`)
            .join("\n")}`;
          showError(null, summary);
        } else {
          const summary = `All ${allErrors.length} disclosures failed:\n${allErrors
            .map((f) => `${f.title}: ${f.reason}`)
            .join("\n")}`;
          showError(null, summary);
        }

        await loadDisclosures();
      } catch (error) {
        showError(
          error,
          error?.message || "Failed to batch upload disclosures"
        );
      } finally {
        setBatchProcessing(false);
      }
    }
  });
  const displayConfig = useMemo(
    () => getDisclosureCenterDisplayConfig(useLargeLayout),
    [useLargeLayout]
  );
  const toolsMenuOpen = Boolean(toolsAnchorEl);

  const loadDisclosures = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listDisclosures({ c_id: "all" });
      const rows = extractList(res);
      setDisclosures(rows);
      setSelectedDisclosure((prev) => {
        if (!prev?.d_id) return prev;
        return rows.find((row) => row.d_id === prev.d_id) || null;
      });
    } catch (error) {
      showError(error, error?.message || "Failed to load disclosures");
      setDisclosures([]);
    } finally {
      setLoading(false);
    }
  }, [listDisclosures, showError]);

  useEffect(() => {
    loadDisclosures();
  }, [loadDisclosures]);

  useEffect(() => {
    if (isMdUp) setTagsDrawerOpen(false);
  }, [isMdUp]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const updateLayoutMode = () => {
      setUseLargeLayout(shouldUseLargeDisclosureCenter());
    };

    updateLayoutMode();
    window.addEventListener("resize", updateLayoutMode);
    window.visualViewport?.addEventListener("resize", updateLayoutMode);

    return () => {
      window.removeEventListener("resize", updateLayoutMode);
      window.visualViewport?.removeEventListener("resize", updateLayoutMode);
    };
  }, []);

  const loadTags = useCallback(async () => {
    setTagsLoading(true);
    try {
      const res = await listDisclosureTags();
      setTags(extractList(res));
    } catch (error) {
      showError(error, error?.message || "Failed to load tags");
      setTags([]);
    } finally {
      setTagsLoading(false);
    }
  }, [listDisclosureTags, showError]);

  useEffect(() => {
    loadTags();
  }, [loadTags]);

  const filteredRows = useMemo(() => {
    let rows = disclosures;
    if (filterActive !== "all") {
      const wantActive = filterActive === "true";
      rows = rows.filter((d) => {
        const active = Boolean(d.is_active);
        return wantActive ? active : !active;
      });
    }
    const s = search.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((d) => {
      const title = (d.title || "").toLowerCase();
      const stmt = (d.statement_text || "").toLowerCase();
      const companyName = (d.company?.name || "").toLowerCase();
      const uen = (d.company?.uen || "").toLowerCase();
      const tagMatch = (d.disclosure_tags || []).some((t) =>
        (t.name || "").toLowerCase().includes(s)
      );
      return (
        title.includes(s) ||
        stmt.includes(s) ||
        companyName.includes(s) ||
        uen.includes(s) ||
        tagMatch
      );
    });
  }, [disclosures, search, filterActive]);

  const openCreate = () => {
    setFormMode("create");
    setSelectedDisclosure(null);
    setFormOpen(true);
  };

  const handleToolsMenuOpen = (event) => {
    setToolsAnchorEl(event.currentTarget);
  };

  const handleToolsMenuClose = () => {
    setToolsAnchorEl(null);
  };

  const handleToolsMenuAction = (action) => {
    handleToolsMenuClose();
    action();
  };

  const openEdit = (d) => {
    setFormMode("edit");
    setSelectedDisclosure(d);
    setFormOpen(true);
  };

  const closeForm = () => {
    if (formSubmitting) return;
    setFormOpen(false);
  };

  const handleFormSubmit = async (payload) => {
    if (formSubmitting) return;

    setFormSubmitting(true);
    try {
      if (formMode === "edit") {
        if (!selectedDisclosure?.d_id) {
          throw new Error("No disclosure selected for update");
        }
        const res = await updateDisclosure(selectedDisclosure.d_id, payload);
        showSuccess(res?.message || "Disclosure updated");
      } else {
        const res = await createDisclosure(payload);
        showSuccess(res?.message || "Disclosure created");
      }

      setFormOpen(false);
      setSelectedDisclosure(null);
      await loadDisclosures();
      await loadTags();
    } catch (error) {
      showError(error, error?.message || "Failed to save disclosure");
    } finally {
      setFormSubmitting(false);
    }
  };

  const openDelete = (d) => {
    setDeleteTarget(d);
    setDeleteOpen(true);
  };

  const closeDelete = () => {
    if (deleteSubmitting) return;
    setDeleteOpen(false);
    setDeleteTarget(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.d_id || deleteSubmitting) return;
    setDeleteSubmitting(true);
    try {
      const res = await deleteDisclosure(deleteTarget.d_id);
      showSuccess(res?.message || "Disclosure deleted");
      setDeleteOpen(false);
      setDeleteTarget(null);
      if (selectedDisclosure?.d_id === deleteTarget.d_id)
        setSelectedDisclosure(null);
      await loadDisclosures();
    } catch (error) {
      showError(error, error?.message || "Failed to delete disclosure");
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const openBulkDeleteDialog = (d_ids) => {
    if (!d_ids?.length) return;
    setBulkDeleteIds(d_ids);
  };

  const bulkDeletePreviewTitles = useMemo(() => {
    if (!bulkDeleteIds?.length) return [];
    const idSet = new Set(bulkDeleteIds.map(String));
    return disclosures
      .filter((d) => idSet.has(String(d.d_id)))
      .map((d) => d.title || "")
      .slice(0, 5);
  }, [bulkDeleteIds, disclosures]);

  const confirmBulkDelete = async () => {
    if (!bulkDeleteIds?.length || bulkDeleteSubmitting) return;
    setBulkDeleteSubmitting(true);
    try {
      const res = await deleteDisclosuresBatch(bulkDeleteIds);
      showSuccess(res?.message || "Disclosures deleted");
      setBulkDeleteIds(null);
      setBulkDeleteSeq((s) => s + 1);
      if (
        selectedDisclosure?.d_id &&
        bulkDeleteIds.some(
          (id) => String(id) === String(selectedDisclosure.d_id)
        )
      ) {
        setSelectedDisclosure(null);
      }
      await loadDisclosures();
    } catch (error) {
      showError(error, error?.message || "Failed to delete disclosures");
    } finally {
      setBulkDeleteSubmitting(false);
    }
  };

  const handleSelectDisclosure = (d) => {
    setSelectedDisclosure(d);
    if (!isMdUp) setTagsDrawerOpen(true);
  };

  const filteredTags = useMemo(() => {
    const q = tagSearch.trim().toLowerCase();
    if (!q) return tags;
    return tags.filter((tag) => (tag.name || "").toLowerCase().includes(q));
  }, [tags, tagSearch]);

  const filteredLibraryTags = useMemo(() => {
    const q = tagLibrarySearch.trim().toLowerCase();
    if (!q) return tags;
    return tags.filter((tag) => (tag.name || "").toLowerCase().includes(q));
  }, [tags, tagLibrarySearch]);

  const tagUsageMap = useMemo(() => {
    const usage = {};
    disclosures.forEach((disclosure) => {
      (disclosure.disclosure_tags || []).forEach((tag) => {
        const key = String(tag.t_id || "");
        if (!key) return;
        usage[key] = (usage[key] || 0) + 1;
      });
    });
    return usage;
  }, [disclosures]);

  const refreshSelectedDisclosure = useCallback((rows, disclosureId) => {
    if (!disclosureId) {
      setSelectedDisclosure(null);
      return;
    }
    const found = rows.find((row) => row.d_id === disclosureId) || null;
    setSelectedDisclosure(found);
  }, []);

  /** Creates an org tag in the library only (no disclosure update). Returns created tag or null. */
  const createTagInLibrary = async () => {
    const name = tagNameInput.trim();
    if (!name || tagsSubmitting) return null;
    setTagsSubmitting(true);
    try {
      const res = await createDisclosureTag({ name, is_system: false });
      const createdTag = res?.data ?? res;
      setTagNameInput("");
      await loadTags();
      showSuccess(res?.message || "Tag created");
      return createdTag;
    } catch (error) {
      showError(error, error?.message || "Failed to create tag");
      return null;
    } finally {
      setTagsSubmitting(false);
    }
  };

  /** Persists tag assignments for the selected disclosure in one update. */
  const saveTagAssignments = async (tag_ids) => {
    if (!selectedDisclosure?.d_id || tagsSubmitting) return false;
    setTagsSubmitting(true);
    try {
      const res = await updateDisclosure(selectedDisclosure.d_id, {
        tag_ids
      });
      showSuccess(res?.message || "Tags saved");
      const refreshed = await listDisclosures({ c_id: "all" });
      const rows = extractList(refreshed);
      setDisclosures(rows);
      refreshSelectedDisclosure(rows, selectedDisclosure.d_id);
      return true;
    } catch (error) {
      showError(error, error?.message || "Failed to save tags");
      return false;
    } finally {
      setTagsSubmitting(false);
    }
  };

  const handleStartEditTag = (tag) => {
    setEditingTagId(tag.t_id);
    setEditingTagName(tag.name || "");
  };

  const handleSaveEditTag = async () => {
    const t_id = editingTagId;
    const name = editingTagName.trim();
    if (!t_id || !name || tagsSubmitting) return;
    setTagsSubmitting(true);
    try {
      const res = await updateDisclosureTag(t_id, { name });
      showSuccess(res?.message || "Tag updated");
      setEditingTagId(null);
      setEditingTagName("");
      await loadTags();
      await loadDisclosures();
    } catch (error) {
      showError(error, error?.message || "Failed to update tag");
    } finally {
      setTagsSubmitting(false);
    }
  };

  const handleDeleteTag = async (tag) => {
    if (!tag?.t_id || tagsSubmitting) return;
    setTagsSubmitting(true);
    try {
      const res = await deleteDisclosureTag(tag.t_id);
      showSuccess(res?.message || "Tag deleted");
      await loadTags();
      await loadDisclosures();
    } catch (error) {
      showError(error, error?.message || "Failed to delete tag");
    } finally {
      setTagsSubmitting(false);
    }
  };

  const handleBatchDeleteTags = async (t_ids) => {
    if (!t_ids?.length || tagsSubmitting) return;
    setTagsSubmitting(true);
    try {
      const res = await deleteDisclosureTagsBatch(t_ids);
      showSuccess(res?.message || "Tags deleted");
      await loadTags();
      await loadDisclosures();
    } catch (error) {
      showError(error, error?.message || "Failed to delete tags");
      throw error;
    } finally {
      setTagsSubmitting(false);
    }
  };

  const handleCreateLibraryTag = async () => {
    const name = tagLibraryNameInput.trim();
    if (!name || tagsSubmitting) return;
    setTagsSubmitting(true);
    try {
      const res = await createDisclosureTag({ name, is_system: false });
      showSuccess(res?.message || "Tag created");
      setTagLibraryNameInput("");
      await loadTags();
    } catch (error) {
      showError(error, error?.message || "Failed to create tag");
    } finally {
      setTagsSubmitting(false);
    }
  };

  const handleStartEditLibraryTag = (tag) => {
    setLibraryEditingTagId(tag.t_id);
    setLibraryEditingTagName(tag.name || "");
  };

  const handleSaveEditLibraryTag = async () => {
    const t_id = libraryEditingTagId;
    const name = libraryEditingTagName.trim();
    if (!t_id || !name || tagsSubmitting) return;
    setTagsSubmitting(true);
    try {
      const res = await updateDisclosureTag(t_id, { name });
      showSuccess(res?.message || "Tag updated");
      setLibraryEditingTagId(null);
      setLibraryEditingTagName("");
      await loadTags();
      await loadDisclosures();
    } catch (error) {
      showError(error, error?.message || "Failed to update tag");
    } finally {
      setTagsSubmitting(false);
    }
  };

  const disclosureTagPanelSharedProps = {
    isMdUp,
    tags,
    tagSearch,
    setTagSearch,
    tagNameInput,
    setTagNameInput,
    createTagInLibrary,
    saveTagAssignments,
    tagsSubmitting,
    tagsLoading,
    filteredTags,
    editingTagId,
    editingTagName,
    setEditingTagName,
    handleSaveEditTag,
    setEditingTagId,
    handleStartEditTag,
    handleDeleteTag,
    setTagsDrawerOpen
  };
  const headerConfig = displayConfig.header;
  const actionButtonSx = {
    fontWeight: 600,
    minHeight: 36,
    textTransform: "none",
    whiteSpace: "nowrap"
  };
  const renderSecondaryToolItems = () => (
    <>
      <MenuItem
        onClick={() => handleToolsMenuAction(() => setTagLibraryOpen(true))}
      >
        <ListItemIcon>
          <LocalOfferIcon fontSize='small' color='primary' />
        </ListItemIcon>
        <ListItemText
          primary='Tags Library'
          secondary='Manage disclosure tags'
          primaryTypographyProps={{ fontWeight: 600 }}
        />
      </MenuItem>
    </>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        bgcolor: "background.default",
        overflow: "hidden"
      }}
    >
      <Box
        sx={{
          px: headerConfig.px,
          py: headerConfig.py,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          flexShrink: 0
        }}
      >
        <Stack
          direction={headerConfig.stackDirection}
          spacing={headerConfig.contentGap}
          alignItems={{
            xs: "stretch",
            md: "center"
          }}
          justifyContent='space-between'
        >
          <Stack
            direction='row'
            alignItems='center'
            spacing={1.25}
            minWidth={0}
            sx={{ flex: "0 1 260px" }}
          >
            <Box
              sx={{
                width: headerConfig.iconSize,
                height: headerConfig.iconSize,
                borderRadius: "12px",
                background: "linear-gradient(135deg, #603F97 0%, #D31D83 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0
              }}
            >
              <SearchIcon sx={{ color: "white", fontSize: 20 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant={headerConfig.titleVariant}
                fontWeight={800}
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }}
              >
                Disclosures Library
              </Typography>
              {!headerConfig.hideSubtitle && (
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                  }}
                >
                  Manage and standardize disclosures for your company.
                </Typography>
              )}
            </Box>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={headerConfig.contentGap}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{
              flex: 1,
              maxWidth: headerConfig.searchMaxWidth,
              minWidth: 0
            }}
          >
            <TextField
              size='small'
              placeholder='Search disclosures, tags, or companies...'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 40,
                  borderRadius: 2,
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "grey.300"
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "primary.main",
                    boxShadow: "0 0 0 3px rgba(96, 63, 151, 0.1)"
                  }
                },
                "& .MuiInputAdornment-positionStart": {
                  ml: 0.5
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon
                      color='action'
                      sx={{
                        fontSize: 20,
                        color: "text.secondary"
                      }}
                    />
                  </InputAdornment>
                )
              }}
            />

            <TextField
              select
              size='small'
              label='Status'
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
              sx={{
                minWidth: headerConfig.statusMinWidth,
                flexShrink: 0,
                "& .MuiOutlinedInput-root": {
                  height: 40,
                  borderRadius: 2
                }
              }}
            >
              <MenuItem value='all'>All</MenuItem>
              <MenuItem value='true'>Active</MenuItem>
              <MenuItem value='false'>Inactive</MenuItem>
            </TextField>
          </Stack>

          <Stack
            direction='row'
            spacing={headerConfig.actionGap}
            alignItems='center'
            justifyContent='flex-end'
            sx={{ flexShrink: 0, flexWrap: "wrap" }}
          >
            {!headerConfig.toolMenu && (
              <>
                <Button
                  variant='outlined'
                  size={headerConfig.actionButtonSize}
                  startIcon={<LocalOfferIcon />}
                  onClick={() => setTagLibraryOpen(true)}
                  sx={actionButtonSx}
                >
                  Tags Library
                </Button>

                <Button
                  variant='outlined'
                  size={headerConfig.actionButtonSize}
                  startIcon={<UploadFileIcon />}
                  onClick={openUploadDialog}
                  sx={actionButtonSx}
                  disabled={batchProcessing || uploading}
                >
                  Batch upload
                </Button>
              </>
            )}
            {headerConfig.toolMenu && (
              <Tooltip title='Batch upload'>
                <span>
                  <IconButton
                    onClick={openUploadDialog}
                    disabled={batchProcessing || uploading}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1.5,
                      color: "success.main",
                      height: 36,
                      width: 36
                    }}
                    aria-label='Batch upload'
                  >
                    <UploadFileIcon fontSize='small' />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {headerConfig.toolMenu && (
              <>
                <Tooltip title='Disclosure tools'>
                  <IconButton
                    onClick={handleToolsMenuOpen}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1.5,
                      height: 36,
                      width: 36
                    }}
                    aria-label='Disclosure tools'
                  >
                    <MoreHorizIcon fontSize='small' />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={toolsAnchorEl}
                  open={toolsMenuOpen}
                  onClose={handleToolsMenuClose}
                  sx={{
                    mt: 1,
                    "& .MuiPaper-root": {
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      boxShadow: "0 12px 32px rgba(15, 23, 42, 0.14)",
                      minWidth: 260
                    }
                  }}
                >
                  <Box sx={{ px: 2, py: 1.25 }}>
                    <Typography variant='subtitle2' fontWeight={700}>
                      Disclosure Tools
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      Library maintenance actions
                    </Typography>
                  </Box>
                  <Divider />
                  {renderSecondaryToolItems()}
                </Menu>
              </>
            )}
            <Button
              variant='contained'
              size={headerConfig.actionButtonSize}
              startIcon={<AddIcon />}
              onClick={openCreate}
              sx={{ ...actionButtonSx, fontWeight: 700 }}
            >
              New disclosure
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          minHeight: 0,
          flexDirection: { xs: "column", md: "row" }
        }}
      >
        <Box
          component='aside'
          sx={{
            flex: { xs: 1, md: "0 0 auto" },
            width: { xs: "100%", md: LIST_COLUMN_WIDTH_MD },
            maxWidth: { xs: "100%", md: 400 },
            borderBottom: { xs: "1px solid", md: "none" },
            bgcolor: "#FFFFFF",
            display: "flex",
            flexDirection: "column",
            minHeight: 0
          }}
        >
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              p: { xs: 1.5, md: 2 },
              bgcolor: "#FAFBFC"
            }}
          >
            {loading ? (
              <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
                <ComponentLoading message='Loading disclosures…' />
              </Box>
            ) : filteredRows.length === 0 ? (
              <Box sx={{ py: 6, textAlign: "center", px: 1 }}>
                <Typography variant='body2' color='text.secondary'>
                  {disclosures.length === 0
                    ? "No disclosures yet."
                    : "No matches."}
                </Typography>
              </Box>
            ) : (
              <DisclosureList
                filteredRows={filteredRows}
                selectedDisclosure={selectedDisclosure}
                handleSelectDisclosure={handleSelectDisclosure}
                onBulkDeleteRequest={openBulkDeleteDialog}
                clearBulkSelectionKey={bulkDeleteSeq}
              />
            )}
          </Box>
        </Box>

        {isMdUp ? (
          <Box
            component='main'
            sx={{
              flex: 1,
              minWidth: { md: "min(480px, 45vw)" },
              display: "flex",
              flexDirection: "column",
              minHeight: 0
            }}
          >
            <DisclosureInspector
              selectedDisclosure={selectedDisclosure}
              openEdit={openEdit}
              openDelete={openDelete}
              disclosureTagPanelSharedProps={disclosureTagPanelSharedProps}
            />
          </Box>
        ) : null}
      </Box>

      {!isMdUp ? (
        <Drawer
          anchor='right'
          open={tagsDrawerOpen}
          onClose={() => setTagsDrawerOpen(false)}
          PaperProps={{
            sx: {
              width: { xs: "80vw", sm: "min(100%, 680px)" },
              maxWidth: "100vw"
            }
          }}
        >
          <DisclosureInspector
            selectedDisclosure={selectedDisclosure}
            openEdit={openEdit}
            openDelete={openDelete}
            disclosureTagPanelSharedProps={disclosureTagPanelSharedProps}
          />
        </Drawer>
      ) : null}

      <DisclosureFormDialog
        open={formOpen}
        mode={formMode}
        submitting={formSubmitting}
        initialValues={selectedDisclosure}
        listDisclosureTags={listDisclosureTags}
        createDisclosureTag={createDisclosureTag}
        getCompanyList={getCompanyList}
        onClose={closeForm}
        onSubmit={handleFormSubmit}
        onError={(error, fallbackMessage) =>
          showError(error, fallbackMessage || "Failed to save disclosure")
        }
      />
      <DisclosureTagLibraryDialog
        open={tagLibraryOpen}
        onClose={() => {
          if (tagsSubmitting) return;
          setTagLibraryOpen(false);
          setTagLibrarySearch("");
          setTagLibraryNameInput("");
          setLibraryEditingTagId(null);
          setLibraryEditingTagName("");
        }}
        tagsLoading={tagsLoading}
        tagsSubmitting={tagsSubmitting}
        filteredTags={filteredLibraryTags}
        search={tagLibrarySearch}
        setSearch={setTagLibrarySearch}
        newTagInput={tagLibraryNameInput}
        setNewTagInput={setTagLibraryNameInput}
        onCreateTag={handleCreateLibraryTag}
        editingTagId={libraryEditingTagId}
        editingTagName={libraryEditingTagName}
        setEditingTagName={setLibraryEditingTagName}
        onStartEditTag={handleStartEditLibraryTag}
        onSaveEditTag={handleSaveEditLibraryTag}
        onCancelEditTag={() => {
          setLibraryEditingTagId(null);
          setLibraryEditingTagName("");
        }}
        onDeleteTag={handleDeleteTag}
        onBatchDeleteTags={handleBatchDeleteTags}
        tagUsageMap={tagUsageMap}
      />

      <DisclosureBulkDeleteDialog
        bulkDeleteIds={bulkDeleteIds}
        setBulkDeleteIds={setBulkDeleteIds}
        deleting={bulkDeleteSubmitting}
        confirmDelete={confirmBulkDelete}
        previewTitles={bulkDeletePreviewTitles}
      />

      <UploadExcelDialog
        title='Disclosures Library'
        open={uploadDialogOpen}
        onClose={closeUploadDialog}
        onDownloadTemplate={downloadDisclosureTemplate}
        onExportExisting={() => exportDisclosuresToExcel(disclosures)}
        exportExistingLabel='Export Existing Disclosures as Excel'
        infoTitle='Smart Update mode'
        infoMessage='If you upload a disclosure with a title that’s already in use, we’ll simply update the existing disclosure for you instead of creating a new one.'
        onFileChange={handleUploadFileChange}
        onUpload={triggerUpload}
        selectedFile={selectedFile}
        uploading={uploading}
        processing={batchProcessing}
        onCloseAlert={closeUploadAlert}
        onReset={handleUploadReset}
        errorFileUrl={errorFileUrl}
        validRows={validRows}
        templateHeadersString={getTemplateHeadersString()}
        templateInstructions={disclosureUploadInstructions}
      />

      <Dialog open={deleteOpen} onClose={closeDelete} maxWidth='xs' fullWidth>
        <DialogTitle>Delete disclosure?</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant='body2' color='text.secondary'>
            This action will permanently remove the disclosure.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDelete} disabled={deleteSubmitting}>
            Cancel
          </Button>
          <Button
            variant='contained'
            color='error'
            onClick={confirmDelete}
            disabled={deleteSubmitting}
          >
            {deleteSubmitting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <SnackbarSnack snack={snack} setSnack={setSnack} />
    </Box>
  );
};

const SnackbarSnack = ({ snack, setSnack }) => {
  // Keep it local to avoid importing more dialog providers.
  if (!snack) return null;
  return (
    <Alert
      severity={snack.severity}
      variant='filled'
      onClose={() => setSnack(null)}
      sx={{
        position: "fixed",
        left: "50%",
        bottom: 24,
        transform: "translateX(-50%)",
        zIndex: 2000,
        minWidth: 320,
        maxWidth: "90vw",
        borderRadius: 2,
        boxShadow: 3
      }}
    >
      {snack.message}
    </Alert>
  );
};

export default DisclosureCenterPage;
